import React, { useState, useEffect, useCallback } from 'react';

import cx from 'classnames';
import {
  CountryCode,
  parsePhoneNumber,
  ENCLOSED_PARENTHESIS_REGEX,
  INTL_FORMAT,
  formatIncompletePhoneNumber,
  dropdownItems,
  isSupportedCountry,
} from 'lane-shared/helpers/phoneNumbers';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import { Dropdown, ValidatedInput } from 'components/form';
import { Flex } from 'components/layout';
import { S } from 'components/typography';

import styles from './VisitorPhoneInput.scss';

type Props = {
  className?: string;
  style?: React.CSSProperties;
  displayName?: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  validation?: {};
  isPristine?: boolean;
  isHidden?: boolean;
  isRequired?: boolean;
  testId?: string;
  isDisabled?: boolean;
  onRegionChange?: (regionCode: CountryCode) => void;
};

export default function VisitorPhoneInput({
  className,
  style,
  displayName,
  placeholder,
  value,
  onChange,
  validation,
  isPristine,
  isHidden = false,
  isRequired = false,
  testId,
  isDisabled = false,
  onRegionChange,
}: Props) {
  const isCountryCodeChangeFixEnabled = useFlag(
    FeatureFlag.PhoneInputCountryCodeChangeFix,
    false
  );
  const browserLanguage = window.navigator.language;
  const browserRegion = new Intl.Locale(browserLanguage).region || '';
  const defaultRegion = isSupportedCountry(browserRegion)
    ? (browserRegion as CountryCode)
    : 'US';
  const [regionCode, setRegionCode] = useState<CountryCode>(defaultRegion);
  const [phoneNumber, setPhoneNumber] = useState(value || '');

  useEffect(() => {
    // load national phone number on initial render if passed
    if (value) {
      const number = parsePhoneNumber(value)?.nationalNumber;

      if (number) {
        setPhoneNumber(number.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegionChange = useCallback((country: CountryCode) => {
    onRegionChange?.(country);
  }, [onRegionChange]);

  function syncValueLegacy() {
    if (value) {
      const country = parsePhoneNumber(value)?.country;

      if (country) {
        setRegionCode(country);
        handleRegionChange(country);
      }
    } else {
      setPhoneNumber(value ?? '');
    }
  }

  useEffect(() => {
    if (!isCountryCodeChangeFixEnabled) {
      syncValueLegacy();

      return;
    }

    if (!value) {
      setPhoneNumber(value ?? '');

      return;
    }

    const parsedPhone = parsePhoneNumber(value);
    const country = parsedPhone?.country;

    if (country && country !== regionCode) {
      setRegionCode(country);
      handleRegionChange(country);
    }

    if (parsedPhone?.nationalNumber) {
      setPhoneNumber(parsedPhone.nationalNumber.toString());
    }
  }, [value, handleRegionChange, isCountryCodeChangeFixEnabled]);

  /* Prevent formatting when deleting closing parenthesis */
  const formattedNumber = phoneNumber?.match(ENCLOSED_PARENTHESIS_REGEX)
    ? phoneNumber
    : formatIncompletePhoneNumber(phoneNumber ?? '', regionCode);

  if (isHidden) return null;

  function handleChange(value: string) {
    setPhoneNumber(value);
    const parsed = parsePhoneNumber(value, regionCode);

    onChange(parsed ? parsed.format(INTL_FORMAT) : value);
  }

  function handleDropdownChange(newRegionCode: CountryCode) {
    setRegionCode(newRegionCode);
    handleRegionChange(newRegionCode);

    if (!isCountryCodeChangeFixEnabled || !phoneNumber) return;

    const parsed = parsePhoneNumber(phoneNumber, newRegionCode);

    onChange(parsed ? parsed.format(INTL_FORMAT) : phoneNumber);
  }

  return (
    <div className={cx(styles.visitorPhoneInput, className)} style={style}>
      <S>
        {displayName}
        {isRequired ? <span className={styles.required}>*</span> : ''}
      </S>
      <Flex className={styles.form}>
        <div className={styles.dropdownWrapper}>
          <Dropdown
            items={dropdownItems}
            placeholder="Country"
            value={regionCode}
            onChange={code => handleDropdownChange(code.value)}
            className={styles.dropdown}
            disabled={isDisabled}
            testId="VisitorPhoneDropdown"
          />
        </div>
        <ValidatedInput
          testId={testId}
          className={styles.inputField}
          value={formattedNumber}
          onChange={handleChange}
          maxLength={16}
          type="tel"
          placeholder={placeholder}
          validation={validation}
          isPristine={isPristine}
          disabled={isDisabled}
        />
      </Flex>
    </div>
  );
}
